import { ManufacturingOrderIntakePayloadDiff } from "@/components/manufacturing/ManufacturingOrderIntakePayloadDiff";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import { CUSTOMER_SALE_PATHS } from "@/components/sales-intake/salesRepIntakeBuild";
import type { OpsOrderIntakeRow, OpsOrderIntakeSalePath } from "@/lib/orderIntakeClient";
import type { IntakeDiff } from "@/lib/orderIntakePayloadDiff";
import type { OrgLocationRow } from "@/lib/locations/fetchOrgLocations";

export type DealerResubmitDraft = {
  salePath: OpsOrderIntakeSalePath | "";
  sellerLocationId: string;
  endCustomerContactId: string;
  notes: string;
};

type Props = {
  selected: OpsOrderIntakeRow;
  draft: DealerResubmitDraft;
  locations: OrgLocationRow[];
  liveDiff: IntakeDiff | null;
  busy: boolean;
  error: string | null;
  success: string | null;
  onDraftChange: (next: DealerResubmitDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
};

/** OI-5b / OI-6b dealer half — revise fields + payload diffs. */
export function DealerOrderResubmitForm({
  selected,
  draft,
  locations,
  liveDiff,
  busy,
  error,
  success,
  onDraftChange,
  onSubmit,
  onCancel,
}: Props) {
  return (
    <div className="space-y-3">
      {selected.hold_reason ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Current hold reason: {selected.hold_reason}
        </div>
      ) : null}
      <ManufacturingOrderIntakePayloadDiff row={selected} />
      {liveDiff && liveDiff.fields.length > 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100">
          <div className="font-medium">What you&apos;re changing vs. last submission</div>
          <ul className="mt-2 space-y-1">
            {liveDiff.fields.map((f) => (
              <li key={f.key}>
                <span className="font-medium">{f.label}:</span>{" "}
                <span className="line-through opacity-70">{f.prior}</span>
                {" → "}
                <span>{f.current}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <label className="block text-xs text-gray-700 dark:text-zinc-300">
        Sale path
        <select
          className={`${inputClass} mt-1`}
          value={draft.salePath}
          disabled={busy}
          onChange={(e) =>
            onDraftChange({ ...draft, salePath: e.target.value as OpsOrderIntakeSalePath | "" })
          }
        >
          <option value="">Select…</option>
          {CUSTOMER_SALE_PATHS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-700 dark:text-zinc-300">
        Seller location
        <select
          className={`${inputClass} mt-1`}
          value={draft.sellerLocationId}
          disabled={busy}
          onChange={(e) => onDraftChange({ ...draft, sellerLocationId: e.target.value })}
        >
          <option value="">Select…</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name || loc.id}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-700 dark:text-zinc-300">
        End customer contact id
        <input
          className={`${inputClass} mt-1 font-mono`}
          value={draft.endCustomerContactId}
          disabled={busy}
          onChange={(e) => onDraftChange({ ...draft, endCustomerContactId: e.target.value })}
        />
      </label>
      <label className="block text-xs text-gray-700 dark:text-zinc-300">
        Notes (optional)
        <textarea
          className={`${inputClass} mt-1 min-h-[64px]`}
          value={draft.notes}
          disabled={busy}
          onChange={(e) => onDraftChange({ ...draft, notes: e.target.value })}
        />
      </label>
      {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}
      {success ? <p className="text-xs text-emerald-700 dark:text-emerald-300">{success}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={primaryBtnClass} disabled={busy} onClick={onSubmit}>
          {busy ? "Resubmitting…" : "Resubmit to manufacturer"}
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
